import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { usePerservedState } from '../utils/versionDataUtil';

import { ReactComponent as DropArrow } from '../assets/svgs/drop-down-arrow.svg';
import { ReactComponent as RefreshIcon } from '../assets/svgs/refresh.svg';
import { ReactComponent as SortIcon } from '../assets/svgs/sort.svg';
import ScrollArrow from '../components/ScrollArrow';

import FilterContainer from '../components/FilterContainer';
import SearchBar from '../components/SearchBar';
import './pages.css';

function BuffsPage({ buffData }) {
    const intl = useIntl();
    const filterRef = useRef();

    const [filteredBuffData, setFilteredBuffData] = useState(new Map());
    const defaultFilterState = {
        searchText: '',
        sortDirection: 'asc',
        filterBy: [],
        filterOpen: false
    };
    const [filterState, setFilterState] = usePerservedState('buffsPageState', defaultFilterState)


    const filterOptions = [
        { value: 'buff', label: intl.formatMessage({ id: 'sort.buff', defaultMessage: 'Buff' }) },
        { value: 'debuff', label: intl.formatMessage({ id: 'sort.debuff', defaultMessage: 'Debuff' }) },
        { value: 'status ailment', label: intl.formatMessage({ id: 'sort.status-ailment', defaultMessage: 'Status Ailment' }) },
        { value: 'special', label: intl.formatMessage({ id: 'sort.special', defaultMessage: 'Special' }) },

    ];

    useEffect(() => {
        let filtered = Object.values(buffData).filter(buff => {
            let searchText = filterState.searchText.toLowerCase()
            const nameMatch = (buff.name ?? '').toLowerCase().includes(searchText);
            const typeMatch = (buff.type ?? '').toLowerCase().includes(searchText);
            const descMatch = (buff.desc ?? '').toLowerCase().includes(searchText);
            const filterMatch = filterState.filterBy.length === 0 || filterState.filterBy.some((filter) => {
                const types = buff.type.toLowerCase().split('/');
                return types.some(type => type === filter.toLowerCase());
            });

            return (nameMatch || typeMatch || descMatch) && filterMatch;
        });
        filtered.sort((a, b) => {
            const sortDirection = filterState.sortDirection || 'asc';
            return sortDirection !== 'asc' ? b.id - a.id : a.id - b.id
        });

        setFilteredBuffData(filtered);
    }, [buffData, filterState]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterState(prevState => ({
                    ...prevState,
                    filterOpen: false,
                }));
            }
        }

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [filterState.filterOpen, setFilterState]);

    const resetFilters = () => {
        setFilterState(defaultFilterState);
    }

    const handleFilterChange = (value) => {
        setFilterState(prevState => {
            const filterBy = prevState.filterBy;
            const valueIndex = filterBy.indexOf(value);

            if (valueIndex > -1) {
                const newFilters = [...filterBy.slice(0, valueIndex), ...filterBy.slice(valueIndex + 1)];
                return { ...prevState, filterBy: newFilters };
            } else {
                return { ...prevState, filterBy: [...filterBy, value] };
            }
        });
    };

    const handleSearchChange = (value) => {
        const newSearchText = value;
        setFilterState(prevState => {
            if (prevState.searchText !== newSearchText) {
                return { ...prevState, searchText: newSearchText };
            }
            return prevState;
        });
    };

    if (Object.keys(buffData).length === 0) {
        return (
            <div className="page">
                <div className="main-container">
                    <div className="surround-margin"></div>
                    <div className="sub-container">
                        <h1 className="main-header">
                            <FormattedMessage
                                id="main.buffs"
                                defaultMessage="Buffs & Status Ailments"
                            />
                            <RefreshIcon className="refresh-icon" />
                        </h1>
                        <div className="main-filter-container">
                            <input
                                type="search"
                                placeholder={intl.formatMessage({ id: 'main.search', defaultMessage: 'Search' })}
                                className="search-bar"
                                value=""
                                readOnly
                            />
                            <div className="buff-filter-container">
                                <div className="buff-title-container">
                                    <div>
                                        <FormattedMessage
                                            id="filter.filter"
                                            defaultMessage="Filter"
                                        />
                                    </div>
                                    <div className="filter-bubble hidden">
                                    </div>
                                    <DropArrow className="drop-down-arrow"></DropArrow>
                                </div>
                                <div className="sort-container">
                                    <SortIcon
                                        className="sort-icon asc"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="table-wrapper">
                            <table className="buff-table shimmer">
                                <thead>
                                    <tr>
                                        <th>{intl.formatMessage({ id: 'buff.icon', defaultMessage: 'Icon' })}</th>
                                        <th>{intl.formatMessage({ id: 'buff.name', defaultMessage: 'Name' })}</th>
                                        <th>{intl.formatMessage({ id: 'buff.type', defaultMessage: 'Type' })}</th>
                                        <th>{intl.formatMessage({ id: 'buff.desc', defaultMessage: 'Description' })}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: 10 }).map((_, index) => (
                                        <tr className="buff-table-row loading" key={index}>
                                            <td className="buff-table-image">
                                            </td>
                                            <td className="table-name"></td>
                                            <td className="table-name"></td>
                                            <td className="table-desc"></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="surround-margin"></div>
                </div>
            </div >
        );
    }

    return (
        <div className="page">
            <div className="main-container">
                <div className="surround-margin"></div>
                <div className="sub-container">
                    <h1 className="main-header">
                        <FormattedMessage
                            id="main.buffs"
                            defaultMessage="Buffs & Status Ailments"
                        />
                        <RefreshIcon onClick={() => resetFilters()} className="refresh-icon" />
                    </h1>
                    <div className="main-filter-container">
                        <SearchBar filterState={filterState} handleSearchChange={handleSearchChange} />
                        <div ref={filterRef} className="buff-filter-container">
                            <div onClick={() => setFilterState(prevState => ({ ...prevState, filterOpen: !prevState.filterOpen }))} className="buff-title-container">
                                <div>
                                    <FormattedMessage
                                        id="filter.filter"
                                        defaultMessage="Filter"
                                    />
                                </div>
                                <div className={`filter-bubble ${filterState.filterBy.length === 0 ? 'hidden' : 'small'}`}>
                                    {filterState.filterBy.length}
                                </div>
                                <DropArrow className="drop-down-arrow"></DropArrow>
                            </div>
                            <div className="sort-container">
                                <FilterContainer filterState={filterState} filterOptions={filterOptions} handleFilterChange={handleFilterChange} />
                                <SortIcon
                                    className={`sort-icon ${filterState.sortDirection}`}
                                    onClick={() => setFilterState(prev => ({ ...prev, sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc' }))}
                                />
                            </div>
                        </div>

                    </div>
                    <div className="table-wrapper">
                        <table className="buff-table">
                            <thead>
                                <tr>
                                    <th>{intl.formatMessage({ id: 'buff.icon', defaultMessage: 'Icon' })}</th>
                                    <th>{intl.formatMessage({ id: 'buff.name', defaultMessage: 'Name' })}</th>
                                    <th>{intl.formatMessage({ id: 'buff.type', defaultMessage: 'Type' })}</th>
                                    <th>{intl.formatMessage({ id: 'buff.desc', defaultMessage: 'Description' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(filteredBuffData).map(([key, buff]) => (
                                    <tr className="buff-table-row" key={buff.name}>
                                        <td className="buff-table-image">
                                            {buff.images.b && buff.images.d ? (
                                                <div className="split-buff-container">
                                                    <img className="buff-image-top" src={buff.images.b} alt={buff.name} />
                                                    <img className={`buff-image-bottom ${buff.type.includes("Status Ailment") ? 'small' : ''}`} src={buff.images.d} alt={buff.name} />
                                                </div>
                                            ) : (
                                                <>
                                                    {buff.images.b && (
                                                        <img className={`buff-page-image ${buff.type.includes("Special") ? 'small' : ''}`} src={buff.images.b} alt={buff.name} />
                                                    )}
                                                    {buff.images.d && (
                                                        <img className={`buff-page-image ${buff.type.includes("Status Ailment") ? 'small' : ''}`} src={buff.images.d} alt={buff.name} />
                                                    )}
                                                </>
                                            )}

                                        </td>
                                        <td className="table-name">{buff.name}</td>
                                        <td className="table-name">
                                            {buff.type.toLowerCase().split('/').map((part, index) => (
                                                <React.Fragment key={index}>
                                                    <FormattedMessage
                                                        id={`buff.${part}`}
                                                        defaultMessage={part}
                                                    />
                                                    {index < buff.type.split('/').length - 1 && "/"}
                                                </React.Fragment>
                                            ))}
                                        </td>
                                        <td className="table-desc">{buff.desc}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <br />
                        <div className="bottom-controls full">
                            <div></div>
                            {Object.keys(filteredBuffData).length > 15 && <div className="bottom-contorls-right"><ScrollArrow className='scroll-arrow' /></div>}
                        </div>
                    </div>
                </div>
                <div className="surround-margin"></div>
            </div>
        </div >
    );
}

export default BuffsPage;